<template>
    <div class="p-4">
        <AccionesCabeceraPagina>
            <div class="d-flex gap-2">
                <button class="btn btn-sm btn-primary" :disabled="creandoParte || !aviso" @click="crearParteDesdeAviso">
                    <span v-if="creandoParte" class="spinner-border spinner-border-sm me-md-1" />
                    <i v-else class="bi bi-plus-lg me-md-1" />
                    <span class="d-none d-md-inline">Nuevo parte</span>
                </button>
                <RouterLink to="/mis-avisos" class="btn btn-sm btn-outline-secondary">
                    <i class="bi bi-arrow-left me-md-1" />
                    <span class="d-none d-sm-inline">Volver</span>
                </RouterLink>
            </div>
        </AccionesCabeceraPagina>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

        <!-- Datos -->
        <template v-else-if="aviso">
            <div v-if="errorCrearParte" class="alert alert-danger">{{ errorCrearParte }}</div>
            <div class="row g-3">
                <!-- Descripción -->
                <div class="col-12">
                    <div class="card">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Descripción</span>
                        </div>
                        <div class="card-body">
                            <p class="small mb-0" style="white-space: pre-wrap;">{{ aviso.descripcion }}</p>
                        </div>
                    </div>
                </div>

                <!-- Info general -->
                <div class="col-12 col-lg-4">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Info general</span>
                        </div>
                        <div class="card-body">
                            <dl class="row mb-0">
                                <dt class="col-5 text-muted fw-normal small">Estado</dt>
                                <dd class="col-7 small">
                                    <span class="badge" :class="aviso.finalizado ? 'bg-success' : 'bg-warning text-dark'">
                                        {{ aviso.finalizado ? 'Cerrado' : 'Abierto' }}
                                    </span>
                                </dd>

                                <dt class="col-5 text-muted fw-normal small">Fecha</dt>
                                <dd class="col-7 small">{{ formatFecha(aviso.fecha) }}</dd>

                                <dt class="col-5 text-muted fw-normal small">Tipo</dt>
                                <dd class="col-7 small">{{ aviso.tipo?.descripcion ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">Canal</dt>
                                <dd class="col-7 small">{{ aviso.canal?.descripcion ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">Urgencia</dt>
                                <dd class="col-7 small">{{ aviso.urgencia?.descripcion ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">Seguimiento</dt>
                                <dd class="col-7 small">{{ aviso.seguimiento?.descripcion ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">Presupuesto h.</dt>
                                <dd class="col-7 small">{{ aviso.presupuestoHoras ?? '—' }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>

                <!-- Cliente -->
                <div class="col-12 col-lg-4">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Cliente</span>
                        </div>
                        <div class="card-body">
                            <dl class="row mb-0">
                                <dt class="col-5 text-muted fw-normal small">Nombre fiscal</dt>
                                <dd class="col-7 small">
                                    <RouterLink
                                        v-if="aviso.cliente?.idc"
                                        :to="`/clientes/${aviso.cliente.idc}`"
                                    >{{ aviso.cliente.nombreFiscal ?? aviso.cliente.idc }}</RouterLink>
                                    <span v-else>—</span>
                                </dd>

                                <dt class="col-5 text-muted fw-normal small">Dirección</dt>
                                <dd class="col-7 small">{{ aviso.cliente?.direccion ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">Teléfono</dt>
                                <dd class="col-7 small">
                                    <a v-if="aviso.cliente?.telefono" :href="`tel:${aviso.cliente.telefono}`">{{ aviso.cliente.telefono }}</a>
                                    <span v-else>—</span>
                                </dd>

                                <dt class="col-5 text-muted fw-normal small">Email</dt>
                                <dd class="col-7 small">
                                    <a v-if="aviso.cliente?.email" :href="`mailto:${aviso.cliente.email}`">{{ aviso.cliente.email }}</a>
                                    <span v-else>—</span>
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                <!-- Seguimiento técnico -->
                <div class="col-12 col-lg-4">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Seguimiento técnico</span>
                        </div>
                        <div class="card-body">
                            <div v-if="errorSeguimiento" class="alert alert-danger py-2 small mb-3">{{ errorSeguimiento }}</div>
                            <div class="mb-3">
                                <label class="form-label small mb-1">Seguimiento</label>
                                <AuxiliarSelect v-model="formSeguimiento.seguimiento" id-tipo-auxiliar="IdSeguimiento" />
                            </div>
                            <div class="mb-3">
                                <label class="form-label small mb-1">Notas</label>
                                <textarea
                                    v-model="formSeguimiento.observaciones2"
                                    class="form-control form-control-sm"
                                    rows="4"
                                    style="resize: vertical;"
                                />
                            </div>
                            <div class="text-end">
                                <button class="btn btn-sm btn-primary" :disabled="guardandoSeguimiento" @click="guardarSeguimiento">
                                    <span v-if="guardandoSeguimiento" class="spinner-border spinner-border-sm me-1" />
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Observaciones -->
                <div v-if="aviso.observaciones" class="col-12">
                    <div class="card">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Observaciones</span>
                        </div>
                        <div class="card-body">
                            <p class="small mb-0" style="white-space: pre-wrap;">{{ aviso.observaciones }}</p>
                        </div>
                    </div>
                </div>

                <!-- Asignaciones -->
                <div class="col-12">
                    <div class="card">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Asignaciones</span>
                        </div>
                        <div class="card-body p-0">
                            <div v-if="aviso.asignaciones.length === 0" class="text-center text-muted py-3 small">
                                Sin asignaciones.
                            </div>
                            <template v-else>
                                <!-- Cards (mobile) -->
                                <div class="d-md-none">
                                    <div
                                        v-for="asignacion in aviso.asignaciones"
                                        :key="asignacion.idAsignacion"
                                        class="border-bottom px-3 py-2 d-flex justify-content-between align-items-center"
                                    >
                                        <div class="d-flex flex-column">
                                            <span class="fw-medium small text-dark">{{ asignacion.usuario?.nombre ?? '—' }}</span>
                                            <span class="text-muted" style="font-size: 0.75rem;">{{ formatFecha(asignacion.fecha) }}</span>
                                        </div>
                                        <i class="bi bi-person-check text-muted fs-5" />
                                    </div>
                                </div>

                                <!-- Tabla (desktop) -->
                                <div class="d-none d-md-block table-responsive">
                                    <table class="table table-sm mb-0">
                                        <thead class="table-light">
                                            <tr>
                                                <th class="ps-3 border-0 small">Usuario</th>
                                                <th class="border-0 small">Fecha</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="asignacion in aviso.asignaciones" :key="asignacion.idAsignacion">
                                                <td class="ps-3 small">{{ asignacion.usuario?.nombre ?? '—' }}</td>
                                                <td class="small text-nowrap">{{ formatFecha(asignacion.fecha) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
                <!-- Partes -->
                <div class="col-12">
                    <div class="card">
                        <div class="card-header d-flex align-items-center justify-content-between py-2">
                            <span class="fw-semibold small">Partes</span>
                            <RouterLink :to="{ name: 'mis-partes', query: { idc: aviso.cliente?.idc, todos: '1' } }" class="small text-muted text-decoration-none">
                                Ver partes del cliente
                            </RouterLink>
                        </div>
                        <div class="card-body p-0">
                            <div v-if="loadingPartes" class="text-center py-3">
                                <div class="spinner-border spinner-border-sm text-secondary" role="status" />
                            </div>
                            <div v-else-if="partes.length === 0" class="text-center text-muted py-3 small">Sin partes.</div>
                            <template v-else>
                                <!-- Cards (mobile) -->
                                <div class="d-md-none overflow-hidden">
                                    <RouterLink
                                        v-for="parte in partes"
                                        :key="parte.idParte"
                                        :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }"
                                        class="border-bottom px-3 py-2 text-decoration-none d-block text-dark"
                                    >
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <span class="text-muted small">#{{ parte.idParte }} · {{ formatFecha(parte.fecha) }}</span>
                                            <span class="badge" :class="parte.cerrado ? 'bg-success' : 'bg-warning text-dark'">
                                                {{ parte.cerrado ? 'Cerrado' : 'Abierto' }}
                                            </span>
                                        </div>
                                        <div class="small">{{ parte.tipoParte?.descripcion ?? '—' }} <span v-if="parte.usuario?.nombre" class="text-muted">· {{ parte.usuario.nombre }}</span></div>
                                        <div v-if="parte.observaciones" class="small text-muted text-clamp-2">{{ parte.observaciones }}</div>
                                        <div v-if="parte.tramosString" class="small text-muted mt-1" v-html="parte.tramosString" />
                                    </RouterLink>
                                </div>

                                <!-- Tabla (desktop) -->
                                <div class="d-none d-md-block table-responsive">
                                    <table class="table table-hover table-sm mb-0">
                                        <thead class="table-light">
                                            <tr>
                                                <th class="ps-3">#</th>
                                                <th>Fecha</th>
                                                <th>Tipo</th>
                                                <th class="d-none d-lg-table-cell">Usuario</th>
                                                <th class="d-none d-lg-table-cell">Observaciones</th>
                                                <th>Tramos</th>
                                                <th>Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="parte in partes"
                                                :key="parte.idParte"
                                                class="row-link"
                                            >
                                                <td class="ps-3 text-muted small">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-muted d-block">
                                                        {{ parte.idParte }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small text-nowrap">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark d-block">
                                                        {{ formatFecha(parte.fecha) }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark d-block">
                                                        {{ parte.tipoParte?.descripcion ?? '—' }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small d-none d-lg-table-cell text-muted">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-muted d-block">
                                                        {{ parte.usuario?.nombre ?? '—' }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small d-none d-lg-table-cell" style="max-width: 220px;">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark d-block">
                                                        <div v-if="parte.observaciones" class="text-clamp-2" :title="parte.observaciones">{{ parte.observaciones }}</div>
                                                        <span v-else class="text-muted">—</span>
                                                    </RouterLink>
                                                </td>
                                                <td class="small" style="white-space: nowrap;">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark d-block" v-html="parte.tramosString || '—'" />
                                                </td>
                                                <td>
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none d-block">
                                                        <span class="badge" :class="parte.cerrado ? 'bg-success' : 'bg-warning text-dark'">
                                                            {{ parte.cerrado ? 'Cerrado' : 'Abierto' }}
                                                        </span>
                                                    </RouterLink>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <!-- Paginación -->
                                <div v-if="partesTotalPages > 1" class="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                                    <span class="small text-muted">{{ partesTotalCount }} partes &middot; Página {{ partesPage }} de {{ partesTotalPages }}</span>
                                    <div class="d-flex gap-1">
                                        <button class="btn btn-sm btn-outline-secondary" :disabled="partesPage <= 1 || loadingPartes" @click="cargarPartes(partesPage - 1)">
                                            <i class="bi bi-chevron-left" />
                                        </button>
                                        <button class="btn btn-sm btn-outline-secondary" :disabled="partesPage >= partesTotalPages || loadingPartes" @click="cargarPartes(partesPage + 1)">
                                            <i class="bi bi-chevron-right" />
                                        </button>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { misAvisosApi } from '@/api/misAvisosApi'
import { misPartesApi } from '@/api/misPartesApi'
import { partesApi } from '@/api/partesApi'
import { avisosApi } from '@/api/avisosApi'
import type { Aviso } from '@/types/aviso'
import type { Auxiliar } from '@/types/auxiliar'
import type { Parte } from '@/types/parte'
import AccionesCabeceraPagina from '@/components/AccionesCabeceraPagina.vue'
import AuxiliarSelect from '@/components/AuxiliarSelect.vue'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import { useConfirm } from '@/composables/useConfirm'

const { confirm } = useConfirm()
const route = useRoute()
const router = useRouter()
const idAviso = Number(route.params.idAviso)
const { title, mobileTitle } = usePageHeader(`#${idAviso}`, `#${idAviso}`)
const { crumbs } = useBreadcrumbs([{ label: 'Mis avisos', to: '/mis-avisos' }, { label: `#${idAviso}` }])

const loading = ref(false)
const error = ref<string | null>(null)
const creandoParte = ref(false)
const errorCrearParte = ref<string | null>(null)
const aviso = ref<Aviso | null>(null)
const partes = ref<Parte[]>([])

const formSeguimiento = ref<{ seguimiento: Auxiliar | null; observaciones2: string }>({
    seguimiento: null,
    observaciones2: '',
})
const guardandoSeguimiento = ref(false)
const errorSeguimiento = ref<string | null>(null)

async function guardarSeguimiento() {
    guardandoSeguimiento.value = true
    errorSeguimiento.value = null
    try {
        const updated = await avisosApi.update(idAviso, {
            seguimiento: formSeguimiento.value.seguimiento,
            observaciones2: formSeguimiento.value.observaciones2 || null,
        })
        aviso.value = updated
    } catch {
        errorSeguimiento.value = 'No se pudo guardar.'
    } finally {
        guardandoSeguimiento.value = false
    }
}
const partesPage = ref(1)
const partesTotalPages = ref(1)
const partesTotalCount = ref(0)
const loadingPartes = ref(false)

const PARTES_PAGE_SIZE = 20

async function cargarPartes(page: number) {
    loadingPartes.value = true
    try {
        const r = await partesApi.getAll({ idAviso, page, pageSize: PARTES_PAGE_SIZE })
        partes.value = r.items
        partesPage.value = r.page
        partesTotalPages.value = r.totalPages
        partesTotalCount.value = r.totalCount
    } finally {
        loadingPartes.value = false
    }
}

function formatFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function getFechaActualIso(): string {
    const hoy = new Date()
    const year = hoy.getFullYear()
    const month = String(hoy.getMonth() + 1).padStart(2, '0')
    const day = String(hoy.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

async function crearParteDesdeAviso() {
    if (!aviso.value || creandoParte.value) {
        return
    }

    const confirmado = await confirm('¿Crear un nuevo parte para este aviso?', 'Crear parte')
    if (!confirmado) {
        return
    }

    creandoParte.value = true
    errorCrearParte.value = null

    try {
        const context = await misPartesApi.getCreateContext({ idAviso })
        const idCliente = context.cliente?.idc
        const idTipoParte = context.defaultTipoParteId

        if (!idCliente) {
            errorCrearParte.value = 'El aviso no tiene un cliente válido para crear el parte.'
            return
        }

        if (!idTipoParte) {
            errorCrearParte.value = 'No hay un tipo de parte por defecto configurado.'
            return
        }

        const parte = await misPartesApi.create({
            idCliente,
            idAviso,
            idTipoParte,
            fecha: getFechaActualIso(),
            observaciones: '',
        })

        await router.push({ name: 'parte-detail', params: { idParte: parte.idParte } })
    } catch {
        errorCrearParte.value = 'No se pudo crear el parte.'
    } finally {
        creandoParte.value = false
    }
}

onMounted(async () => {
    loading.value = true
    error.value = null
    try {
        const [avisoData] = await Promise.all([
            misAvisosApi.getById(idAviso),
            cargarPartes(1),
        ])
        aviso.value = avisoData
        formSeguimiento.value = {
            seguimiento: avisoData?.seguimiento ?? null,
            observaciones2: avisoData?.observaciones2 ?? '',
        }
        const label = aviso.value?.cliente?.nombreFiscal
            ? `#${idAviso} · ${aviso.value.cliente.nombreFiscal}`
            : `#${idAviso}`
        title.value = label
        mobileTitle.value = `#${idAviso}`
        crumbs.value[1].label = label
    } catch {
        error.value = 'No se pudo cargar el aviso.'
    } finally {
        loading.value = false
    }
})
</script>
